import { AlertTriangle, CheckCircle, Clock, MapPin, ArrowRight, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const EmergencyRecoveryPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Emergency Car Recovery Hackney | 24/7 Urgent Vehicle Recovery East London"
        description="24/7 emergency car recovery in Hackney & East London. Accident recovery, urgent breakdown assistance, immediate vehicle collection. Call 07492 639223 now for fast help."
        keywords="emergency car recovery hackney, urgent vehicle recovery, accident recovery east london, immediate car recovery, 24/7 emergency breakdown, urgent towing service hackney"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-700 via-red-600 to-red-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🚨 Emergency Response Available Now
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Emergency Car Recovery in Hackney & East London
            </h1>
            <p className="text-xl text-orange-50 mb-8">
              Urgent breakdown, accident, or vehicle emergency? We provide immediate 24/7 emergency recovery services across Hackney and East London. Average 30-minute response time for all urgent vehicle recovery situations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-red-900 hover:bg-slate-100 text-lg px-8 py-6 animate-pulse">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Call Now: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">24/7</p>
              <p className="text-sm text-slate-600">Always Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">30 Min</p>
              <p className="text-sm text-slate-600">Emergency Response</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">Licensed</p>
              <p className="text-sm text-slate-600">Fully Insured</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">Local</p>
              <p className="text-sm text-slate-600">Hackney Based</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              24/7 Emergency Vehicle Recovery Service in Hackney
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              When you need urgent vehicle recovery, every second counts. Ovawell Autos provides immediate 24/7 emergency car recovery services across Hackney, Shoreditch, Stratford and all East London. Whether it's an accident, severe breakdown, or urgent situation, our emergency response team reaches you within 30 minutes on average.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Emergency Situations We Handle
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Road traffic accidents",
                "Vehicle fires (after fire service clearance)",
                "Severe mechanical failures",
                "Vehicle stuck in unsafe location",
                "Flooded engine recovery",
                "Collision recovery",
                "Police-requested vehicle removal",
                "Urgent towing needed",
                "Vehicle blocking access",
                "Stranded on motorway/major road",
                "Security-related urgent moves",
                "Any roadside emergency"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Our Emergency Recovery Service?
            </h3>
            <div className="space-y-4 mb-8">
              <Card className="border-red-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-red-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Immediate Response - 24/7/365</h4>
                      <p className="text-slate-600">
                        Our emergency line is answered immediately, 24 hours a day, 7 days a week, including Christmas Day. We understand that emergencies don't wait for office hours.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-red-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-red-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">30-Minute Average Arrival</h4>
                      <p className="text-slate-600">
                        Based locally in Hackney, we reach most East London emergencies within 30 minutes. Our drivers know every street and fastest route to get to you quickly.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-red-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Shield className="w-8 h-8 text-red-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Professional & Discreet</h4>
                      <p className="text-slate-600">
                        Our experienced drivers handle every emergency with professionalism. Fully licensed, insured recovery vehicles. We secure your vehicle and possessions safely.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              What to Do in a Vehicle Emergency
            </h3>
            <Card className="mb-8 bg-orange-50 border-orange-200">
              <CardContent className="p-6">
                <ol className="space-y-3 list-decimal list-inside text-slate-700">
                  <li><strong>Ensure Safety First:</strong> If possible, move vehicle to safe location. Turn on hazard lights.</li>
                  <li><strong>Call Emergency Services if Needed:</strong> For accidents with injuries, call 999 first.</li>
                  <li><strong>Call Ovawell Autos:</strong> Contact us immediately on {companyInfo.phone}</li>
                  <li><strong>Provide Location:</strong> Tell us your exact location and describe the situation</li>
                  <li><strong>Stay Safe:</strong> If on a busy road, stay behind barriers or well away from traffic</li>
                  <li><strong>Wait for Our Team:</strong> We'll arrive within 30 minutes on average</li>
                </ol>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Emergency Recovery Areas Covered
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Hackney E5, E8, E9",
                "Shoreditch E1, E2",
                "Stratford E15, E20",
                "Bethnal Green E2",
                "Bow E3",
                "Mile End E3",
                "Dalston E8",
                "Homerton E9",
                "Hackney Wick E9",
                "Clapton E5",
                "Stoke Newington N16",
                "Islington N1",
                "Tower Hamlets E1",
                "London Fields E8",
                "Victoria Park E9",
                "Plus 20-mile radius"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Our Emergency Recovery Process
            </h3>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-600">1</span>
                  </div>
                  <h4 className="font-bold mb-2">Call Us</h4>
                  <p className="text-sm text-slate-600">Immediate answer 24/7. Describe your emergency</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-600">2</span>
                  </div>
                  <h4 className="font-bold mb-2">We Dispatch</h4>
                  <p className="text-sm text-slate-600">Nearest recovery vehicle sent immediately</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-600">3</span>
                  </div>
                  <h4 className="font-bold mb-2">Fast Arrival</h4>
                  <p className="text-sm text-slate-600">30-min average across Hackney & East London</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-red-600">4</span>
                  </div>
                  <h4 className="font-bold mb-2">Safe Recovery</h4>
                  <p className="text-sm text-slate-600">Vehicle secured and transported to your location</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Emergency Recovery Pricing
            </h3>
            <Card className="mb-8">
              <CardContent className="p-6">
                <p className="text-slate-700 mb-4">
                  Emergency recovery pricing depends on distance, time, and specific situation. We provide upfront pricing before dispatch - no surprises.
                </p>
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  <p className="text-slate-900 font-semibold mb-2">Transparent Pricing Promise:</p>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>✓ Price quoted before we dispatch</li>
                    <li>✓ No hidden fees or surprise charges</li>
                    <li>✓ Same 24/7 rates - no night surcharges</li>
                    <li>✓ Payment after safe vehicle delivery</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Do you charge more for night/weekend emergencies?</h4>
                  <p className="text-slate-600">
                    No. Our emergency recovery service has the same rates 24/7, including nights, weekends, and bank holidays. Emergencies don't wait for business hours, and neither should fair pricing.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">How quickly can you reach me?</h4>
                  <p className="text-slate-600">
                    Our average response time for emergencies in Hackney and East London is 30 minutes. For urgent situations on major roads or dangerous locations, we prioritize even faster response.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Can you recover vehicles from accident scenes?</h4>
                  <p className="text-slate-600">
                    Yes. We work with emergency services and can recover vehicles from accident scenes once cleared by police. We handle damaged vehicles with care.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Where can you take my vehicle?</h4>
                  <p className="text-slate-600">
                    We can transport your vehicle to your home, a garage of your choice, or secure storage. Just let us know your preferred destination.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-red-700 to-orange-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Emergency Recovery? Call Now
          </h2>
          <p className="text-xl text-red-50 mb-8 max-w-2xl mx-auto">
            Available 24/7 for immediate emergency vehicle recovery across Hackney & East London.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-red-900 hover:bg-slate-100 text-lg px-8 py-6">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Emergency: {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-900 text-lg px-8 py-6">
                Get Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
